import { useIsPresent } from "motion/react";
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { CountState } from "../api/useSegmentCounts";
import { formatConditionBadge } from "../conditionBadge";
import { SEGMENT_LIMITS, type Logic } from "../constants";
import { useRestoreOrder } from "../hooks/useRestoreOrder";
import { useUndoToast } from "../hooks/useUndoToast";
import { createEmptyCondition } from "../mappers";
import type { SegmentFormValues } from "../schemas";
import { ConditionEditor } from "./ConditionEditor";
import { ConditionParts } from "./ConditionParts";
import { RuleBlockView, type RuleSnapshot } from "./RuleBlockView";

import type { TourDemoControl } from "@/features/segments/tour/constants";
import type { RuleOverlap, SegmentField } from "@/features/segments/types";
import useToast from "@/lib/hooks/useToast";

type RuleBlockProps = {
  ruleIndex: number;
  readOnly: boolean;
  clientId?: string;
  fields: SegmentField[];
  count: CountState;
  activeConditionKey: string | null;
  onActiveConditionChange: (key: string | null) => void;
  overlap: RuleOverlap;
  tourDemoControl: TourDemoControl | null;
  onRemove: () => void;
  editedHint?: string;
};

type LiveRuleBlockProps = RuleBlockProps & {
  snapshotRef: MutableRefObject<RuleSnapshot | undefined>;
};

type FocusRequest = { conditionKey?: string };

const ADD_CONDITION_SELECTOR = "li[data-add-condition] > button";

const noop = () => undefined;

function LiveRuleBlock({
  ruleIndex,
  readOnly,
  clientId,
  fields,
  count,
  activeConditionKey,
  onActiveConditionChange,
  overlap,
  tourDemoControl,
  onRemove,
  editedHint,
  snapshotRef,
}: LiveRuleBlockProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const showUndoToast = useUndoToast();
  const restoreOrder = useRestoreOrder();
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<SegmentFormValues>();
  const rule = watch(`rules.${ruleIndex}`);
  const {
    append: appendCondition,
    insert: insertCondition,
    remove: removeCondition,
  } = useFieldArray({
    control,
    name: `rules.${ruleIndex}.conditions`,
  });
  const latest = useRef({ ruleIndex, insertCondition });

  const badgeListRef = useRef<HTMLUListElement>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);

  const ruleErrors = errors.rules?.[ruleIndex];
  const conditions = rule.conditions;
  const invalidConditions = conditions.map(
    (_, index) => !!ruleErrors?.conditions?.[index],
  );
  const conditionsMessage = ruleErrors?.conditions?.message;
  const matchMessage = ruleErrors?.type?.message;

  useEffect(() => {
    snapshotRef.current = {
      rule,
      invalidConditions,
      conditionsMessage,
      matchMessage,
    };
    latest.current = { ruleIndex, insertCondition };
  });

  useEffect(() => {
    if (!focusRequest) return;
    const selector = focusRequest.conditionKey
      ? `[data-condition-key="${focusRequest.conditionKey}"]`
      : ADD_CONDITION_SELECTOR;
    badgeListRef.current?.querySelector<HTMLElement>(selector)?.focus();
    setFocusRequest(null);
  }, [focusRequest]);

  const handleMatchChange = (type: Logic) =>
    setValue(`rules.${ruleIndex}.type`, type, {
      shouldDirty: true,
      shouldValidate: true,
    });

  const handleAddCondition = () => {
    const condition = createEmptyCondition();
    appendCondition(condition);
    onActiveConditionChange(condition.clientKey);
  };

  const handleOpenChange = (clientKey: string, open: boolean) => {
    if (open) {
      onActiveConditionChange(clientKey);
      return;
    }
    if (activeConditionKey !== clientKey) return;
    onActiveConditionChange(null);
  };

  const handleRemoveCondition = (conditionIndex: number) => {
    const removed = getValues(
      `rules.${ruleIndex}.conditions.${conditionIndex}`,
    );
    const countBefore = conditions.length;
    const neighbour =
      conditions[conditionIndex + 1] ?? conditions[conditionIndex - 1];
    const parts = formatConditionBadge(removed, fields, t);
    restoreOrder.track(conditions.map((condition) => condition.clientKey));
    onActiveConditionChange(null);
    removeCondition(conditionIndex);
    setFocusRequest({ conditionKey: neighbour?.clientKey });
    showUndoToast({
      message: t("admin.segments.builder.conditionRemoved"),
      description: parts && <ConditionParts parts={parts} />,
      onUndo: () => {
        const { ruleIndex: currentRuleIndex, insertCondition: insert } =
          latest.current;
        const current = getValues(`rules.${currentRuleIndex}.conditions`);
        if (
          current.length >= Math.max(SEGMENT_LIMITS.conditions, countBefore)
        ) {
          showToast(
            t("admin.segments.builder.limit.conditions", {
              count: SEGMENT_LIMITS.conditions,
            }),
            "warning",
          );
          return;
        }
        const currentKeys = current.map((condition) => condition.clientKey);
        insert(restoreOrder.indexFor(removed.clientKey, currentKeys), removed);
      },
    });
  };

  return (
    <RuleBlockView
      ruleIndex={ruleIndex}
      rule={rule}
      invalidConditions={invalidConditions}
      conditionsMessage={conditionsMessage}
      matchMessage={matchMessage}
      readOnly={readOnly}
      fields={fields}
      count={count}
      activeConditionKey={activeConditionKey}
      overlap={overlap}
      badgeListRef={badgeListRef}
      renderEditor={(conditionIndex) => (
        <ConditionEditor
          id={`condition-editor-${conditions[conditionIndex]?.clientKey}`}
          ruleIndex={ruleIndex}
          conditionIndex={conditionIndex}
          fields={fields}
          clientId={clientId}
          tourDemoControl={tourDemoControl}
          onRemove={() => handleRemoveCondition(conditionIndex)}
        />
      )}
      onMatchChange={handleMatchChange}
      onAddCondition={handleAddCondition}
      onRemoveCondition={handleRemoveCondition}
      onOpenChange={handleOpenChange}
      onRemove={onRemove}
      editedHint={editedHint}
    />
  );
}

export function RuleBlock(props: RuleBlockProps) {
  const isPresent = useIsPresent();
  const snapshotRef = useRef<RuleSnapshot>();

  if (isPresent || !snapshotRef.current) {
    return <LiveRuleBlock {...props} snapshotRef={snapshotRef} />;
  }

  return (
    <RuleBlockView
      {...snapshotRef.current}
      ruleIndex={props.ruleIndex}
      readOnly={props.readOnly}
      exiting
      fields={props.fields}
      count={props.count}
      activeConditionKey={null}
      renderEditor={() => null}
      onMatchChange={noop}
      onAddCondition={noop}
      onRemoveCondition={noop}
      onOpenChange={noop}
      onRemove={noop}
    />
  );
}
